import React, { ReactElement, useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  Tooltip,
  Typography,
  SelectChangeEvent,
} from '@mui/material';
import { Close, Edit as EditIcon } from '@mui/icons-material';
import MuiAlert from '@mui/material/Alert';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import {
  gray40,
  gray60,
  gray80,
  white,
  yellow50,
  yellow_overlay10,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { useProxyDialogSx } from '../../../hooks/useProxyDialogSx';
import {
  boldMd,
  regularXl,
  regularLg,
  regularSm,
  formLabelWrapper,
} from '../../../constants/location';
import CustomTextfield from '../form/CustomTextfield';
import CustomSelectField from '../form/CustomSelectField';
import { displayWidth as displayWidthEnum } from '../../../constants/enum';
import {
  dataTypeOptions as dataTypeOptionsFromConstants,
  dateOutputFormatOptions,
  DEFAULT_DATE_OUTPUT_FORMAT,
} from '../../../constants/options';
import { DEFAULT_DATE_EXTRACTION_RULE } from '../../../constants/promptTemplates';
import {
  itemValidationLabel,
  itemValidationHelperText,
  supportsLengthConstraint,
  supportsValueRangeConstraint,
  supportsZeroAsEmpty,
  supportsCharTypeConstraint,
  supportsReviewChoice,
  supportsDateRangeConstraint,
  dateRangeUnit,
  ITEM_VALIDATION_PATTERN_MAX_LENGTH,
  isChoiceDataType,
  isCalculatableDataType,
  parentOnlyDataTypes,
  fixedValuePlaceholder,
  type DateRangeUnit,
  type ItemCharType,
} from '../../../constants/itemValidation';
import AdvancedValidationSettings, { CHAR_TYPE_NONE } from './parts/AdvancedValidationSettings';
import {
  sanitizeFieldCode,
  validateFieldCode,
  fieldCodeErrorMessages,
  FIELD_CODE_SEPARATOR,
} from '../../../utils/pattern/fieldCode';
import { FIELD_CODE_MAX_LENGTH } from '../../../constants/enum';
import FormulaEditor, { type FormulaReferenceItem } from '../parts/FormulaEditor';
import ChoiceEditor from '../parts/ChoiceEditor';
import { sanitizeChoices, validateChoices } from '../../../utils/pattern/choiceUtils';
import { ItemChoiceType } from '../../../types/common';

// 計算フィールド設定（#3145）を編集するためのコンテキスト。
// 未指定の場合は計算設定のUIを表示しない（結果画面など段階導入前の呼び出し元向け）
export type CalculationEditContext = {
  /** この項目の式から参照できる項目（参照スコープ適用済み・自分自身は除く） */
  referenceableItems: FormulaReferenceItem[];
  /** SUM で集計できる明細の列（#3358）。単一項目の編集時のみ入る */
  aggregatableItems?: FormulaReferenceItem[];
  /** パターン内の全フィールドコード（スコープ違反の判定用） */
  allFieldCodes: string[];
};

/**
 * 未設定のフィールドコードの初期値を作る（#3175）。
 * 子項目は `親のコード.子の名前` にして、保存時の自動採番（fillMissingFieldCodes）と
 * 同じ値になるようにする。連番（_2 等）は他項目との重複時に保存時へ付くため、ここでは付けない。
 */
const buildDefaultFieldCode = (itemName: string, parentFieldCode?: string): string => {
  const own = sanitizeFieldCode(itemName);
  if (!parentFieldCode) return own;
  return Array.from(`${parentFieldCode}${FIELD_CODE_SEPARATOR}${own}`)
    .slice(0, FIELD_CODE_MAX_LENGTH)
    .join('');
};

export type EditItemData = {
  id: string;
  itemName: string;
  dataType: string;
  displayWidth: number;
  isChild: boolean;
  parentId?: string;
  listIndex?: number;
  childIndex?: number;
  isOcrTarget?: boolean; // OCR対象フラグ（#2968）。false は手入力欄。デフォルト: true
  extractionRule?: string;
  outputFormat?: string;
  fieldCode?: string; // フィールドコード（#3175）。計算フィールド・指定フォーマット出力の識別子
  /** 子項目の場合の親のフィールドコード（#3175）。未設定時の初期値を `親.子` にするために使う */
  parentFieldCode?: string;
  /** 自分以外の項目が使用中のフィールドコード（重複チェック用・#3175） */
  usedFieldCodes?: string[];
  fixedValue?: string; // 固定値（#3073）。値が取得できなかった場合に入れる値
  choices?: ItemChoiceType[]; // 選択肢（#3075）。ラジオボタン・プルダウン型のときのみ使用
  // 項目バリデーション制約（#2815）
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  minValue?: number;
  maxValue?: number;
  treatZeroAsEmpty?: boolean; // 0を空欄扱い（#3262）
  // 文字種・入力パターン・日付範囲（#3357）
  reviewChoiceValues?: string[]; // 要注意にする選択肢（#3357）
  reviewLowConfidence?: boolean; // 読み取り精度が低いときに要注意にするか（#3384）。未設定は ON
  charType?: ItemCharType;
  pattern?: string;
  patternMessage?: string;
  dateNoPast?: boolean;
  dateNoFuture?: boolean;
  datePastLimit?: number;
  datePastUnit?: DateRangeUnit;
  dateFutureLimit?: number;
  dateFutureUnit?: DateRangeUnit;
  // 計算フィールド設定（#3145）
  isCalculated?: boolean;
  formula?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSave: (data: EditItemData) => void;
  onSaveAndReOcr?: (data: EditItemData) => void;
  itemData: EditItemData | null;
  saveButtonLabel?: string;
  isCustomPrompt?: boolean;
  // 計算フィールド設定（#3145）。未指定なら計算設定UIを表示しない
  calculationContext?: CalculationEditContext;
  /**
   * この結果に適用されている子パターンの名称（#2967）。
   * 指定すると案内を出し、子パターン側で設定するよう誘導する
   */
  appliedSubPatternName?: string | null;
  /** 子パターンの編集を開く。未指定なら案内内のリンクを出さない（#2967） */
  onEditSubPattern?: () => void;
};

// constants/options.tsの選択肢を表示用ラベルに変換
const dataTypeOptions = dataTypeOptionsFromConstants.map(opt => ({
  value: opt.option, // 表示用ラベル（テキスト、数字、日付、複数行データ）
  option: opt.option,
  description: opt.description,
}));

// 明細（複数行データ）の列で選べるデータ型のみに絞った選択肢（#3075）。
// 選択肢型はテーブルのセル内で扱えないため、子項目の編集時はこちらを使う
const childDataTypeOptions = dataTypeOptions.filter(opt => {
  const internalValue = dataTypeOptionsFromConstants.find(o => o.option === opt.option)?.value;
  return internalValue === undefined || !parentOnlyDataTypes.includes(internalValue);
});

// 表示用ラベル（テキスト等）→ 内部値（string等）への変換
const dataTypeLabelToValue = (label: string): string => {
  const found = dataTypeOptionsFromConstants.find(opt => opt.option === label);
  return found?.value ?? 'string';
};

const displayWidthOptions = [
  { value: displayWidthEnum.large, option: '列幅:大' },
  { value: displayWidthEnum.medium, option: '列幅:中' },
  { value: displayWidthEnum.small, option: '列幅:小' },
];

// 日付出力形式の選択肢（表示に例を含める）
const outputFormatSelectOptions = dateOutputFormatOptions.map(opt => ({
  value: opt.value,
  option: `${opt.option}（${opt.example}）`,
}));

const styles = {
  buttonWrapper: {
    display: 'flex',
    gap: '16px',
    padding: '16px 24px',
    borderTop: `1px solid ${gray40}`,
    backgroundColor: white,
    flexShrink: 0,
    '@media screen and (max-width:960px)': {
      gap: '8px',
      padding: '12px 16px',
    },
  },
  cancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    flex: 1,
    minWidth: 0,
  },
  cancelButtonText: {
    ...boldMd,
    color: gray80,
  },
  saveButton: {
    height: '44px',
    flex: 1,
    minWidth: 0,
    backgroundColor: '#FF6B35',
    '&:hover': {
      backgroundColor: '#E55A2B',
    },
    '&:disabled': {
      backgroundColor: '#FFB299',
      cursor: 'not-allowed',
    },
  },
  saveButtonText: {
    ...boldMd,
    color: white,
  },
  dialog: {
    '& > .MuiDialog-container > .MuiPaper-root': {
      border: '1px solid' + gray40,
      borderRadius: '8px',
      boxSizing: 'border-box',
      margin: '16px',
      padding: 0,
      width: '560px',
      maxWidth: '90vw',
      maxHeight: 'calc(100vh - 32px)',
      display: 'flex',
      flexDirection: 'column',
    },
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '24px 24px 16px',
    flexShrink: 0,
    '@media screen and (max-width:960px)': {
      padding: '24px 16px 12px',
    },
  },
  content: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '0 24px',
    '@media screen and (max-width:960px)': {
      padding: '0 16px',
    },
  },
  icon: {
    height: '32px',
    width: '32px',
    '@media screen and (max-width:960px)': {
      height: '24px',
      width: '24px',
    },
  },
  iconButton: {
    padding: 0,
  },
  title: {
    ...regularXl,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  formField: {
    marginBottom: '20px',
  },
};

/**
 * 子パターンの案内（#2967）。SubPatternChip と同じ白背景・黒枠で、
 * 「子パターンに関する表示」であることを見た目で揃える
 */
const subPatternNoticeStyles = {
  // AI読取の上書き警告（PatternAdd）と同じ黄色。ここで直しても効かない旨の注意のため
  subPatternNotice: {
    marginBottom: '20px',
    color: yellow50,
    backgroundColor: yellow_overlay10,
    textAlign: 'left',
    alignItems: 'flex-start',
    '& .MuiAlert-icon': {
      color: yellow50,
    },
    '& .MuiAlert-message': {
      textAlign: 'left',
    },
  },
  subPatternNoticeText: {
    fontSize: '13px',
    lineHeight: 1.8,
  },
  subPatternNoticeButton: {
    marginTop: '8px',
    minWidth: 'auto',
    padding: '4px 12px',
    backgroundColor: white,
    border: '1px solid ' + yellow50,
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: white,
      opacity: 0.8,
    },
  },
  subPatternNoticeButtonText: {
    fontSize: '13px',
    color: yellow50,
    whiteSpace: 'nowrap',
  },
};

const ItemEditModal = ({
  open,
  onClose,
  onSave,
  onSaveAndReOcr,
  itemData,
  saveButtonLabel,
  isCustomPrompt,
  calculationContext,
  appliedSubPatternName,
  onEditSubPattern,
}: Props): ReactElement => {
  // 項目が多いと maxHeight: calc(100vh - 32px) まで伸びるダイアログのため、
  // 代理モードではバナー分を差し引かないと下端がはみ出す（#3310）
  const proxyDialogSx = useProxyDialogSx({ fullHeight: true });
  const [itemName, setItemName] = useState('');
  const [dataType, setDataType] = useState('テキスト');
  const [displayWidth, setDisplayWidth] = useState<number>(displayWidthEnum.medium);
  const [extractionRule, setExtractionRule] = useState('');
  const [outputFormat, setOutputFormat] = useState(DEFAULT_DATE_OUTPUT_FORMAT);
  const [isOcrTarget, setIsOcrTarget] = useState(true); // OCR対象トグル（#2968）
  const [fixedValue, setFixedValue] = useState(''); // 固定値（#3073）
  const [choices, setChoices] = useState<ItemChoiceType[]>([]); // 選択肢（#3075）
  const [itemNameTouched, setItemNameTouched] = useState(false);
  // 項目バリデーション制約（#2815）。数値入力は空文字許容のため文字列stateで保持する
  const [required, setRequired] = useState(false);
  // 0を空欄として扱うか（#3262）。required と組み合わせたときのみ意味を持つ
  const [treatZeroAsEmpty, setTreatZeroAsEmpty] = useState(false);
  const [minLength, setMinLength] = useState('');
  const [maxLength, setMaxLength] = useState('');
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  // 文字種・入力パターン・日付範囲（#3357）。数値は空文字許容のため文字列stateで保持する
  const [charType, setCharType] = useState<ItemCharType | typeof CHAR_TYPE_NONE>(CHAR_TYPE_NONE);
  // 要注意にする選択肢（#3357）。選択肢の出力値で保持する
  const [reviewChoiceValues, setReviewChoiceValues] = useState<string[]>([]);
  // 読み取り精度が低いときに要注意にするか（#3384）。デフォルト ON
  const [reviewLowConfidence, setReviewLowConfidence] = useState(true);
  const [pattern, setPattern] = useState('');
  const [patternMessage, setPatternMessage] = useState('');
  const [dateNoPast, setDateNoPast] = useState(false);
  const [dateNoFuture, setDateNoFuture] = useState(false);
  const [datePastLimit, setDatePastLimit] = useState('');
  const [datePastUnit, setDatePastUnit] = useState<DateRangeUnit>(dateRangeUnit.year);
  const [dateFutureLimit, setDateFutureLimit] = useState('');
  const [dateFutureUnit, setDateFutureUnit] = useState<DateRangeUnit>(dateRangeUnit.year);
  // フィールドコード（#3175）。誤編集を防ぐため、鉛筆アイコンを押すまでは非活性にする（kintone と同じ挙動）。
  // 確定値（fieldCode）と編集中の下書き（fieldCodeDraft）を分け、キャンセルで下書きを捨てられるようにする。
  // ここで「設定」しても Firestore には書かれない。実際の保存はパターンの保存時。
  const [fieldCode, setFieldCode] = useState('');
  const [fieldCodeDraft, setFieldCodeDraft] = useState('');
  const [isFieldCodeEditable, setIsFieldCodeEditable] = useState(false);
  // 計算フィールド設定（#3145）
  const [isCalculated, setIsCalculated] = useState(false);
  const [formula, setFormula] = useState('');
  const [isFormulaValid, setIsFormulaValid] = useState(false);

  // 数値入力文字列を number | undefined に変換（空文字・不正値は undefined）
  const parseNumericInput = (value: string): number | undefined => {
    if (value.trim() === '') return undefined;
    const num = Number(value);
    return Number.isNaN(num) ? undefined : num;
  };

  useEffect(() => {
    if (itemData) {
      setItemName(itemData.itemName);
      setDataType(itemData.dataType || 'テキスト');
      setDisplayWidth(itemData.displayWidth || displayWidthEnum.medium);
      setExtractionRule(itemData.extractionRule || '');
      setOutputFormat(itemData.outputFormat || DEFAULT_DATE_OUTPUT_FORMAT);
      setIsOcrTarget(itemData.isOcrTarget ?? true);
      setFixedValue(itemData.fixedValue || '');
      // 選択肢（#3075）。選択肢型なのに未設定なら、入力を始められるよう空行を1件用意する
      const initialChoices = itemData.choices ?? [];
      setChoices(
        isChoiceDataType(dataTypeLabelToValue(itemData.dataType)) && initialChoices.length === 0
          ? [{ label: '', value: '' }]
          : initialChoices,
      );
      setItemNameTouched(false);
      setRequired(itemData.required ?? false);
      setTreatZeroAsEmpty(itemData.treatZeroAsEmpty ?? false);
      setMinLength(itemData.minLength !== undefined ? String(itemData.minLength) : '');
      setMaxLength(itemData.maxLength !== undefined ? String(itemData.maxLength) : '');
      setMinValue(itemData.minValue !== undefined ? String(itemData.minValue) : '');
      setMaxValue(itemData.maxValue !== undefined ? String(itemData.maxValue) : '');
      // 文字種・入力パターン・日付範囲（#3357）
      setCharType(itemData.charType ?? CHAR_TYPE_NONE);
      setReviewChoiceValues(itemData.reviewChoiceValues ?? []); // #3357
      setReviewLowConfidence(itemData.reviewLowConfidence ?? true); // #3384
      setPattern(itemData.pattern ?? '');
      setPatternMessage(itemData.patternMessage ?? '');
      setDateNoPast(itemData.dateNoPast ?? false);
      setDateNoFuture(itemData.dateNoFuture ?? false);
      setDatePastLimit(itemData.datePastLimit !== undefined ? String(itemData.datePastLimit) : '');
      setDatePastUnit(itemData.datePastUnit ?? dateRangeUnit.year);
      setDateFutureLimit(
        itemData.dateFutureLimit !== undefined ? String(itemData.dateFutureLimit) : '',
      );
      setDateFutureUnit(itemData.dateFutureUnit ?? dateRangeUnit.year);
      // フィールドコード（#3175）。未設定なら項目名から作る。
      // 子項目は `親のコード.子の名前` にして、保存後の採番結果と表示を一致させる。
      // 既存パターン（導入前に作成）を開いたときもここで補完され、保存するまで Firestore には書かれない。
      const initialFieldCode =
        itemData.fieldCode || buildDefaultFieldCode(itemData.itemName, itemData.parentFieldCode);
      setFieldCode(initialFieldCode);
      setFieldCodeDraft(initialFieldCode);
      setIsFieldCodeEditable(false); // 開き直すたびに編集ロックへ戻す
      // 計算フィールド設定（#3145）
      setIsCalculated(itemData.isCalculated ?? false);
      setFormula(itemData.formula ?? '');
    }
  }, [itemData]);

  // データ型の内部値（string / textarea / number / date / array / radio / select）
  const dataTypeValue = dataTypeLabelToValue(dataType);
  const showLengthConstraint = supportsLengthConstraint(dataTypeValue);
  const showValueRangeConstraint = supportsValueRangeConstraint(dataTypeValue);
  // 0 を空欄として扱う（#3262）。数字型かつ必須ONのときだけ意味を持つ
  const showZeroAsEmpty = supportsZeroAsEmpty(dataTypeValue) && required;
  // 文字種・入力パターン（テキスト系）と日付範囲（日付型）の出し分け（#3357）
  const showCharTypeConstraint = supportsCharTypeConstraint(dataTypeValue);
  // 要注意にする選択肢（#3357）。選択肢型のみ
  const showReviewChoice = supportsReviewChoice(dataTypeValue);
  const showDateRangeConstraint = supportsDateRangeConstraint(dataTypeValue);
  // 読み取り精度の要注意（#3384）。AIで読み取る項目だけが対象。
  // テーブル（複数行データ）の親自体は値を持たないため、列（子項目）ごとに設定する
  const showReviewLowConfidence = dataTypeValue !== 'array' && isOcrTarget;

  // 入力パターンが正規表現として妥当か（#3357）。不正なら保存させない
  const patternError = ((): string | undefined => {
    if (!showCharTypeConstraint || pattern === '') return undefined;
    if (pattern.length > ITEM_VALIDATION_PATTERN_MAX_LENGTH) {
      return `入力パターンは${ITEM_VALIDATION_PATTERN_MAX_LENGTH}文字以内で入力してください`;
    }
    try {
      new RegExp(pattern);
      return undefined;
    } catch {
      return '正規表現として正しくありません';
    }
  })();
  // 選択肢設定（#3075）。ラジオボタン・プルダウンのときだけ表示する。
  // 明細（複数行データ）の列では選択肢型そのものを選べないため、子項目では出ない
  const showChoices = isChoiceDataType(dataTypeValue);
  // 出力設定（#3073）の表示制御。テーブル型は固定値の対象外、列幅は子項目のみ。
  // 中身が両方とも無いときは見出しごと非表示にする
  const showFixedValue = dataTypeValue !== 'array';
  const showOutputSettings = showFixedValue || !!itemData?.isChild;
  // 計算フィールド設定（#3145）。calculationContext が渡されたときだけUIを出す。
  // 対象はテキスト・テキストエリア・数値のみ（#3195）。
  // 日付・選択肢型に計算式を設定しても意味のある結果にならず、
  // テーブル（複数行データ）の親自体は値を持たない（列＝子項目ごとに設定する）
  const showCalculationSettings =
    calculationContext !== undefined && isCalculatableDataType(dataTypeValue);

  // 制約の矛盾チェック（最小 > 最大）
  const minLengthNum = parseNumericInput(minLength);
  const maxLengthNum = parseNumericInput(maxLength);
  const minValueNum = parseNumericInput(minValue);
  const maxValueNum = parseNumericInput(maxValue);
  const lengthRangeError =
    showLengthConstraint &&
    minLengthNum !== undefined &&
    maxLengthNum !== undefined &&
    minLengthNum > maxLengthNum;
  const valueRangeError =
    showValueRangeConstraint &&
    minValueNum !== undefined &&
    maxValueNum !== undefined &&
    minValueNum > maxValueNum;
  const hasValidationError = lengthRangeError || valueRangeError;

  // フィールドコードの検証（#3175）。自分以外が使用中のコードと重複していないかも見る
  const usedFieldCodeSet = useMemo(
    () => new Set(itemData?.usedFieldCodes ?? []),
    [itemData?.usedFieldCodes],
  );
  // 編集中は下書きを、確定後は確定値を検証する。
  // 「設定」を押すまで確定値は変わらないため、編集を中断しても保存は妨げられない。
  const fieldCodeError = validateFieldCode(
    isFieldCodeEditable ? fieldCodeDraft : fieldCode,
    usedFieldCodeSet,
  );

  /** 「設定」= 下書きをモーダル内の確定値にする（Firestore へは書かない） */
  const handleApplyFieldCode = (): void => {
    if (validateFieldCode(fieldCodeDraft, usedFieldCodeSet) !== null) return;
    setFieldCode(fieldCodeDraft);
    setIsFieldCodeEditable(false);
  };

  /** 「キャンセル」= 下書きを捨てて確定値へ戻す */
  const handleCancelFieldCode = (): void => {
    setFieldCodeDraft(fieldCode);
    setIsFieldCodeEditable(false);
  };

  // フィールドコード欄の説明文。エラー時はその内容に差し替える
  const fieldCodeHelperText =
    fieldCodeError !== null
      ? fieldCodeErrorMessages[fieldCodeError]
      : '計算フィールドや指定フォーマット出力から、この項目を指定するための名称です。変更すると、この項目を使っている計算式やフォーマットが正しく動かなくなる場合があります';

  // 選択肢のバリデーション（#3075）。選択肢型以外では検証しない
  const choiceError = showChoices ? validateChoices(sanitizeChoices(choices)) : null;

  // バリデーション: すべてのフィールドが必須。制約の矛盾があると保存不可
  const itemNameError = itemNameTouched && itemName.trim() === '';
  const isValid =
    itemName.trim() !== '' &&
    dataType !== '' &&
    displayWidth !== undefined &&
    displayWidth !== null &&
    !hasValidationError &&
    choiceError === null &&
    fieldCodeError === null &&
    // 計算フィールドは式にエラーが無いことが保存の条件（#3145）
    (!showCalculationSettings || !isCalculated || isFormulaValid) &&
    // 入力パターンが正規表現として不正なら保存させない（#3357）
    patternError === undefined;

  // 制約値の子パターン判定用に、保存される正規化済みの値を求める
  const normalizedRequired = required;
  // 0を空欄扱いは必須ONのときだけ意味を持つ。必須OFFなら保存しない（#3262）
  const normalizedTreatZeroAsEmpty = showZeroAsEmpty ? treatZeroAsEmpty : undefined;
  const normalizedMinLength = showLengthConstraint ? minLengthNum : undefined;
  const normalizedMaxLength = showLengthConstraint ? maxLengthNum : undefined;
  const normalizedMinValue = showValueRangeConstraint ? minValueNum : undefined;
  const normalizedMaxValue = showValueRangeConstraint ? maxValueNum : undefined;
  // 文字種・入力パターン・日付範囲（#3357）。型に合わない値・空値は保存しない
  const normalizedCharType =
    showCharTypeConstraint && charType !== CHAR_TYPE_NONE ? (charType as ItemCharType) : undefined;
  const normalizedPattern = showCharTypeConstraint && pattern !== '' ? pattern : undefined;
  // エラー文言はパターンが設定されているときだけ意味を持つ
  const normalizedPatternMessage =
    normalizedPattern !== undefined && patternMessage !== '' ? patternMessage : undefined;
  const normalizedDateNoPast = showDateRangeConstraint && dateNoPast ? true : undefined;
  const normalizedDateNoFuture = showDateRangeConstraint && dateNoFuture ? true : undefined;
  const datePastLimitNum = parseNumericInput(datePastLimit);
  const dateFutureLimitNum = parseNumericInput(dateFutureLimit);
  // 過去（未来）を一切許可しないなら、さかのぼれる（先に進める）上限は意味を持たない。
  // UI でも非活性にしているが、既存データに両方が入っている場合に備えて保存時にも落とす
  const normalizedDatePastLimit =
    showDateRangeConstraint && !dateNoPast ? datePastLimitNum : undefined;
  const normalizedDatePastUnit = normalizedDatePastLimit !== undefined ? datePastUnit : undefined;
  const normalizedDateFutureLimit =
    showDateRangeConstraint && !dateNoFuture ? dateFutureLimitNum : undefined;
  const normalizedDateFutureUnit =
    normalizedDateFutureLimit !== undefined ? dateFutureUnit : undefined;

  // バリデーション制約に変更があるか（再読取要否・保存活性の判定に使用）
  const hasValidationChange =
    itemData &&
    ((itemData.required ?? false) !== normalizedRequired ||
      itemData.minLength !== normalizedMinLength ||
      itemData.maxLength !== normalizedMaxLength ||
      itemData.minValue !== normalizedMinValue ||
      itemData.maxValue !== normalizedMaxValue ||
      (itemData.treatZeroAsEmpty ?? false) !== (normalizedTreatZeroAsEmpty ?? false) ||
      // 文字種・日付範囲はプロンプトに反映するため再読取が必要（#3357）。
      // 入力パターン（正規表現）はプロンプトに載せないため、ここには含めない
      // （設定を直すたびに再読取＝課金が走るのを避ける）
      itemData.charType !== normalizedCharType ||
      (itemData.dateNoPast ?? false) !== (normalizedDateNoPast ?? false) ||
      (itemData.dateNoFuture ?? false) !== (normalizedDateNoFuture ?? false) ||
      itemData.datePastLimit !== normalizedDatePastLimit ||
      itemData.datePastUnit !== normalizedDatePastUnit ||
      itemData.dateFutureLimit !== normalizedDateFutureLimit ||
      itemData.dateFutureUnit !== normalizedDateFutureUnit);

  // 列幅のみの変更かどうかを判定（ボタンラベル切り替え用）
  // 項目名・データの種類・取得ルール・出力形式・バリデーション制約の変更は再読取が必要
  // OCR対象の切り替えは読取結果が変わるため再読取が必要な変更として扱う（#2968）
  const hasOcrTargetChange = itemData && (itemData.isOcrTarget ?? true) !== isOcrTarget;

  // フィールドコードの変更（#3175）。AI読取の内容は変わらないため再読取は不要
  const hasFieldCodeChange =
    itemData &&
    (itemData.fieldCode || buildDefaultFieldCode(itemData.itemName, itemData.parentFieldCode)) !==
      fieldCode;

  // 固定値の変更（#3073）。AIプロンプトには影響せず読取後に補完するだけなので再読取は不要
  const normalizedFixedValue = dataTypeValue === 'array' ? '' : fixedValue;
  const hasFixedValueChange = itemData && (itemData.fixedValue || '') !== normalizedFixedValue;

  // 計算フィールド設定の変更（#3145）。計算はOCR後に適用するため再読取は不要
  const hasCalculationChange =
    itemData &&
    showCalculationSettings &&
    ((itemData.isCalculated ?? false) !== isCalculated ||
      (itemData.formula ?? '') !== (isCalculated ? formula : ''));

  // 選択肢の変更（#3075）。候補はプロンプトに載りAIの読取結果そのものを変えるため再読取が必要。
  // 選択肢型以外に切り替えたときは空配列にクリアする（古い候補を残さない）
  const normalizedChoices = showChoices ? sanitizeChoices(choices) : [];

  /**
   * 要注意にする選択肢の候補（#3357）。
   * 選択肢の入力欄は空行を1つ持つことがある（新規追加時）ため、
   * 中身のある選択肢だけを候補にする
   */
  const reviewChoiceOptions = normalizedChoices.filter(choice => choice.label !== '');

  // 要注意にする選択肢（#3357）。選択肢を消した・変えた後に取り残された値を落とす
  const normalizedReviewChoiceValues = showReviewChoice
    ? reviewChoiceValues.filter(value =>
        reviewChoiceOptions.some(choice => (choice.value || choice.label) === value),
      )
    : [];
  // 読み取り精度の要注意（#3384）。デフォルト ON のため、OFF のときだけ保存する
  const normalizedReviewLowConfidence =
    showReviewLowConfidence && !reviewLowConfidence ? false : undefined;
  const hasChoicesChange =
    itemData &&
    JSON.stringify(sanitizeChoices(itemData.choices ?? [])) !== JSON.stringify(normalizedChoices);

  /**
   * 再読取が不要な設定の変更（#3357 / #3358）。
   *
   * 入力パターン（正規表現）とエラー文言はプロンプトに載せないため、
   * 変えても読み取り直す必要はない（hasValidationChange に入れると
   * 設定を直すたびに再読取＝課金が走る）。
   * 要注意にする選択肢も、選ばれた値の判定に使うだけで読み取り内容は変わらない。
   * 読み取り精度の要注意（#3384）も判定に使うだけなので同じ扱い。
   *
   * ただし**保存はできなければならない**ため、保存ボタンの活性判定
   * （hasAnyChange）にはこちらを含める。ここを分けておかないと
   * 「設定したのに保存ボタンが押せない」状態になる。
   */
  const hasNonReReadValidationChange =
    itemData &&
    (itemData.pattern !== normalizedPattern ||
      itemData.patternMessage !== normalizedPatternMessage ||
      JSON.stringify(itemData.reviewChoiceValues ?? []) !==
        JSON.stringify(normalizedReviewChoiceValues) ||
      (itemData.reviewLowConfidence ?? true) !== (normalizedReviewLowConfidence ?? true));

  const isNonReReadChange =
    itemData &&
    itemData.itemName === itemName.trim() &&
    itemData.dataType === dataType &&
    (itemData.extractionRule || '') === extractionRule &&
    (itemData.outputFormat || DEFAULT_DATE_OUTPUT_FORMAT) === outputFormat &&
    !hasValidationChange &&
    !hasOcrTargetChange &&
    !hasChoicesChange &&
    (itemData.displayWidth !== displayWidth ||
      hasFieldCodeChange ||
      hasFixedValueChange ||
      hasCalculationChange ||
      // 正規表現・エラー文言・要注意にする選択肢は再読取不要（#3357 / #3358）
      hasNonReReadValidationChange);

  // 何かしら変更があるかどうかを判定（保存ボタンの活性制御用）
  const hasAnyChange =
    itemData &&
    (itemData.itemName !== itemName.trim() ||
      itemData.dataType !== dataType ||
      (itemData.extractionRule || '') !== extractionRule ||
      (itemData.outputFormat || DEFAULT_DATE_OUTPUT_FORMAT) !== outputFormat ||
      itemData.displayWidth !== displayWidth ||
      hasValidationChange ||
      hasOcrTargetChange ||
      hasFieldCodeChange ||
      hasFixedValueChange ||
      hasCalculationChange ||
      hasChoicesChange ||
      // 再読取は不要だが保存は必要な変更（#3357 / #3358）
      hasNonReReadValidationChange);

  const buildSaveData = (): EditItemData | null => {
    if (!itemData || !isValid) return null;
    return {
      ...itemData,
      itemName: itemName.trim(),
      dataType,
      displayWidth,
      isOcrTarget,
      // #3175。「設定」で確定した値のみを渡す（編集中の下書きは反映しない）。
      // ここでも Firestore には書かれず、実際の保存はパターンの保存時。
      fieldCode,

      // OCR対象OFFの項目はAIで読み取らないため取得ルールは保存しない（#2968）
      extractionRule: isOcrTarget ? extractionRule : '',
      outputFormat: dataType === '日付' ? outputFormat : undefined,
      // 固定値（#3073）。テーブル型（複数行データ）は行数が可変で固定値を入れる対象が定まらないため保存しない
      fixedValue: normalizedFixedValue,
      // 選択肢（#3075）。選択肢型以外に変更した場合は空配列でクリアされる
      choices: normalizedChoices,
      // データ型に対応する制約のみ保存する（型変更時に不整合な制約はクリアされる）
      required: normalizedRequired,
      minLength: normalizedMinLength,
      maxLength: normalizedMaxLength,
      minValue: normalizedMinValue,
      maxValue: normalizedMaxValue,
      treatZeroAsEmpty: normalizedTreatZeroAsEmpty,
      // 文字種・入力パターン・日付範囲（#3357）
      // 要注意にする選択肢（#3357）。選択肢型以外・現在の選択肢に無い値は保存しない
      reviewChoiceValues: normalizedReviewChoiceValues,
      // 読み取り精度の要注意（#3384）。OFF のときだけ false、それ以外は未設定（= ON）
      reviewLowConfidence: normalizedReviewLowConfidence,
      charType: normalizedCharType,
      pattern: normalizedPattern,
      patternMessage: normalizedPatternMessage,
      dateNoPast: normalizedDateNoPast,
      dateNoFuture: normalizedDateNoFuture,
      datePastLimit: normalizedDatePastLimit,
      datePastUnit: normalizedDatePastUnit,
      dateFutureLimit: normalizedDateFutureLimit,
      dateFutureUnit: normalizedDateFutureUnit,
      // 計算フィールド設定（#3145）。計算OFFにしたら式もクリアする
      ...(showCalculationSettings
        ? {
            isCalculated,
            formula: isCalculated ? formula : '',
          }
        : {}),
    };
  };

  const handleSave = (): void => {
    const data = buildSaveData();
    if (!data) return;
    onSave(data);
  };

  const handleSaveAndReOcr = (): void => {
    const data = buildSaveData();
    if (!data || !onSaveAndReOcr) return;
    onSaveAndReOcr(data);
  };

  const handleDataTypeChange = (event: SelectChangeEvent<unknown>): void => {
    const newDataType = event.target.value as string;
    setDataType(newDataType);
    // 日付型に変更した場合、取得ルールが空ならデフォルトの日付解釈ルールを設定
    if (newDataType === '日付' && !extractionRule.trim()) {
      setExtractionRule(DEFAULT_DATE_EXTRACTION_RULE);
    }
    // 日付型から他の型に変更した場合、自動入力した日付解釈ルールが残らないようクリアする（#3325）。
    // 手入力・編集済みの取得ルールは利用者の意図なので残す
    if (
      dataType === '日付' &&
      newDataType !== '日付' &&
      extractionRule.trim() === DEFAULT_DATE_EXTRACTION_RULE.trim()
    ) {
      setExtractionRule('');
    }
    // 選択肢型に変更した場合、入力を始められるよう空行を1件用意する（#3075）。
    // ラジオボタン⇔プルダウンの切り替えは表示形式が変わるだけなので選択肢はそのまま残す
    if (isChoiceDataType(dataTypeLabelToValue(newDataType)) && choices.length === 0) {
      setChoices([{ label: '', value: '' }]);
    }
  };

  const handleDisplayWidthChange = (event: SelectChangeEvent<unknown>): void => {
    setDisplayWidth(event.target.value as number);
  };

  const handleOutputFormatChange = (event: SelectChangeEvent<unknown>): void => {
    setOutputFormat(event.target.value as string);
  };

  return (
    <Dialog open={open} onClose={onClose} sx={[styles.dialog, proxyDialogSx]}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>項目設定の編集</Typography>
        <IconButton onClick={onClose} sx={styles.iconButton}>
          <Close sx={styles.icon} />
        </IconButton>
      </Box>

      <Box sx={styles.content}>
        {/*
          子パターンで読み取られた結果の場合の案内（#2967）。
          ここで項目設定を直しても、この帳票には子パターンの設定が優先されるため、
          子パターン側の編集へ誘導する
        */}
        {appliedSubPatternName && (
          <MuiAlert
            severity="warning"
            icon={<ErrorOutlineIcon />}
            sx={subPatternNoticeStyles.subPatternNotice}
          >
            <Typography sx={subPatternNoticeStyles.subPatternNoticeText}>
              {`この結果は子パターン「${appliedSubPatternName}」で読み取られています。この帳票の取得ルールは子パターン側の設定が使われます。`}
            </Typography>
            {onEditSubPattern && (
              <Box>
                <Button
                  onClick={onEditSubPattern}
                  sx={subPatternNoticeStyles.subPatternNoticeButton}
                >
                  <Typography sx={subPatternNoticeStyles.subPatternNoticeButtonText}>
                    子パターンの設定を開く
                  </Typography>
                </Button>
              </Box>
            )}
          </MuiAlert>
        )}

        {isCustomPrompt && (
          <Box sx={{ marginBottom: '20px' }}>
            <Typography sx={{ fontSize: '13px', color: gray80, lineHeight: '1.8' }}>
              事務局によって、カスタムプロンプトで指定されています。
            </Typography>
            <Typography sx={{ fontSize: '13px', color: gray80, lineHeight: '1.8' }}>
              項目設定を変更したい場合は、事務局にご相談ください。
            </Typography>
          </Box>
        )}

        <Box sx={styles.formField}>
          <CustomTextfield
            labelText="項目名"
            value={itemName}
            onChange={(e): void => {
              const nextItemName = e.target.value;
              setItemName(nextItemName);
              if (!itemNameTouched) setItemNameTouched(true);
              // 初回保存前（コード未設定）の項目に限り、項目名の変更にコードを追随させる（#3175）。
              // 保存済みのコードは変えない。変えると計算式・指定フォーマットの参照が壊れるため。
              if (!itemData?.fieldCode) {
                const nextCode = buildDefaultFieldCode(nextItemName, itemData?.parentFieldCode);
                setFieldCode(nextCode);
                setFieldCodeDraft(nextCode);
              }
            }}
            onBlur={(): void => setItemNameTouched(true)}
            inputWidth="100%"
            height="56px"
            error={itemNameError}
            helperText={itemNameError ? '必須です' : undefined}
            disabled={isCustomPrompt}
          />
        </Box>

        <Box sx={{ marginBottom: '20px' }}>
          <CustomSelectField
            labelText="データの種類"
            // 明細の列では選択肢型（ラジオボタン・プルダウン）を選べない（#3075）。
            // セル内に収まらず、プルダウンのみ許可すると設定と表示が食い違うため
            list={itemData?.isChild ? childDataTypeOptions : dataTypeOptions}
            value={dataType}
            onChange={handleDataTypeChange}
            height="56px"
            fontSize="16px"
            disabled={isCustomPrompt || (!itemData?.isChild && dataType === '複数行データ')}
          />
        </Box>

        {dataType === '日付' && (
          <Box sx={{ marginBottom: itemData?.isChild ? '20px' : '32px' }}>
            <CustomSelectField
              labelText="出力形式"
              list={outputFormatSelectOptions}
              value={outputFormat}
              onChange={handleOutputFormatChange}
              height="56px"
              fontSize="16px"
              disabled={isCustomPrompt}
            />
            <Typography
              sx={{ fontSize: '12px', color: gray80, marginTop: '6px', lineHeight: '1.8' }}
            >
              希望の形式がない場合は、データの種類を「テキスト」に変更し、取得ルールで出力形式を指定してください
            </Typography>
          </Box>
        )}

        {/* 選択肢設定（#3075）。ラジオボタン・プルダウンで選ばせる候補を定義する。
            候補はOCRプロンプトにも載るため、AI読取ONなら読み取る値もここに限定される */}
        {showChoices && (
          <>
            <Divider sx={{ borderColor: gray40, marginBottom: '20px' }} />
            <Box sx={{ marginBottom: '20px' }}>
              <ChoiceEditor
                choices={choices}
                onChange={setChoices}
                disabled={isCustomPrompt}
                errorMessage={choiceError}
              />
            </Box>
          </>
        )}

        {/* 基本設定と読み取り設定の区切り（#2968） */}
        <Divider sx={{ borderColor: gray40, marginBottom: '20px' }} />

        {/* 読み取り設定の見出し（#3073）。バリデーション設定の見出しと同じ体裁に揃える */}
        <Typography sx={{ ...boldMd, color: gray80, marginBottom: '8px' }}>読み取り設定</Typography>

        {/* AI読取するトグル（OCR対象フラグ is_ocr_target・#2968）。OFFにするとAIで読み取らず手入力欄になる。
            複数行データ（テーブル）は列（子項目）ごとに設定するため、親テーブル自体には表示しない。
            見た目は辞書変換機能の設定トグル（CompanyDetail）に合わせる。
            ラベルフォントは「データの種類」ラベル（gray80/14px）に揃え、トグルは右端に配置 */}
        {dataTypeValue !== 'array' && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              gap: '8px',
              marginBottom: '20px',
            }}
          >
            <Box>
              <Typography sx={{ ...regularSm, color: gray80 }}>AI読取する</Typography>
              {/* OFFのときの値の入り方は固定値・マスタ照合・辞書変換の設定で変わるため、
                  ここではトグル自体の役割だけを述べる汎用的な文言にする（#3073）。
                  計算フィールドON時はフォールバック挙動（§4.8）を案内する */}
              <Typography sx={{ fontSize: '12px', color: gray80, lineHeight: '1.8' }}>
                {isCalculated
                  ? 'ONにすると計算結果が空のときAIの読み取り値を使います（デフォルトOFF）'
                  : 'OFFの項目はAI読取の対象外になります'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '-6px' }}>
              <Switch
                checked={isOcrTarget}
                onChange={(e): void => setIsOcrTarget(e.target.checked)}
                disabled={isCustomPrompt}
              />
              <Typography sx={{ ...regularSm, color: gray80, minWidth: '32px' }}>
                {isOcrTarget ? 'ON' : 'OFF'}
              </Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ marginBottom: '20px' }}>
          <CustomTextfield
            labelText="取得ルール（任意）"
            value={extractionRule}
            onChange={(e): void => setExtractionRule(e.target.value)}
            inputWidth="100%"
            height="56px"
            placeholder="値の取得条件を入力（任意）"
            multiline
            rows={4}
            // OCR対象OFFの項目は取得ルールを非活性にする。ただしmultiline（内部でTextareaAutosize）＋disabled
            // 切替で、入力枠の高さと、inline-flexな入力枠のベースライン位置がON/OFFで変わり下の項目がズレる。
            // 下記sxで入力枠・textareaの高さを固定し、さらにvertical-align:topでベースライン依存を断って
            // ズレを止める（#2968。各対応の詳細は下のsx内コメント参照）。
            disabled={isCustomPrompt || !isOcrTarget}
            sx={{
              '&.MuiInputBase-multiline': {
                padding: '0 !important',
                height: '124px !important',
                minHeight: '124px !important',
                overflow: 'hidden',
                // 入力枠ルートはinline-flex。親（line-height:24px）のベースライン揃えにより
                // ルート下にline-height由来の隙間ができ、その量がdisabled時のtextareaのベースライン変化で
                // ON/OFFで変わってしまう。vertical-align:topでベースライン依存を断ち、隙間を固定する（#2968）
                verticalAlign: 'top',
              },
              // 可視・測定用シャドウの両textareaを固定pxで完全に釘付けにする。
              // autosize（＋disabled）が裏で高さを動かしてもレイアウトに影響させない。
              // シャドウはposition:absolute/visibility:hiddenなのでレイアウトに出ず、
              // 固定px指定なので以前の100%指定のような測定破壊（400px暴走）も起きない（#2968）
              '& .MuiInputBase-inputMultiline': {
                height: '124px !important',
                minHeight: '124px !important',
                maxHeight: '124px !important',
                boxSizing: 'border-box',
                overflow: 'auto',
              },
            }}
          />
          <Box sx={{ marginTop: '6px' }}>
            <Typography sx={{ fontSize: '12px', color: gray80, lineHeight: '1.8' }}>
              「宛先」の設定例）「送り先」「届け先」「納品先」など別の名称で記載されていても取得
            </Typography>
            <Typography sx={{ fontSize: '12px', color: gray80, lineHeight: '1.8' }}>
              「金額」の設定例）税込み金額をカンマなしの数値で取得
            </Typography>
            <Typography sx={{ fontSize: '12px', color: gray80, lineHeight: '1.8' }}>
              「日付」の設定例）和暦の場合は西暦に変換して YYYY/MM/DD で取得
            </Typography>
          </Box>
        </Box>

        {/* 計算設定（#3145）。読み取り設定とは独立したセクションにする（PR #3174 レビュー反映）。
            計算フィールドにするトグルをONにするとAI読取はデフォルトOFFになる（操作は可能。設計書 §4.8） */}
        {showCalculationSettings && calculationContext && (
          <>
            <Divider sx={{ borderColor: gray40, marginBottom: '20px' }} />
            <Box sx={{ marginBottom: '20px' }}>
              <Typography sx={{ ...boldMd, color: gray80, marginBottom: '8px' }}>
                計算設定
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  gap: '8px',
                  marginBottom: isCalculated ? '20px' : 0,
                }}
              >
                <Box>
                  <Typography sx={{ ...regularSm, color: gray80 }}>計算フィールドにする</Typography>
                  <Typography sx={{ fontSize: '12px', color: gray80, lineHeight: '1.8' }}>
                    他の項目の値から自動で計算します
                  </Typography>
                </Box>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '-6px' }}
                >
                  <Switch
                    checked={isCalculated}
                    onChange={(e): void => {
                      setIsCalculated(e.target.checked);
                      // 計算フィールドのAI読取はデフォルトOFF（設計書 §4.8）。ONに戻すのは任意
                      if (e.target.checked) setIsOcrTarget(false);
                    }}
                    disabled={isCustomPrompt}
                  />
                  <Typography sx={{ ...regularSm, color: gray80, minWidth: '32px' }}>
                    {isCalculated ? 'ON' : 'OFF'}
                  </Typography>
                </Box>
              </Box>

              {/* 計算式の入力欄。入力のたびに構文チェックし、エラーのままでは保存できない */}
              {isCalculated && (
                <FormulaEditor
                  value={formula}
                  onChange={setFormula}
                  referenceableItems={calculationContext.referenceableItems}
                  aggregatableItems={calculationContext.aggregatableItems}
                  allFieldCodes={calculationContext.allFieldCodes}
                  selfFieldCode={fieldCode}
                  selfChoices={normalizedChoices}
                  onValidationChange={setIsFormulaValid}
                  disabled={isCustomPrompt}
                />
              )}
            </Box>
          </>
        )}

        {/* 読み取り設定とバリデーション設定の区切り（#2815） */}
        <Divider sx={{ borderColor: gray40, marginBottom: '20px' }} />

        {/* バリデーション設定（#2815） */}
        <Box sx={{ marginBottom: '20px' }}>
          <Typography sx={{ ...boldMd, color: gray80, marginBottom: '8px' }}>
            バリデーション設定（任意）
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={required}
                onChange={(e): void => setRequired(e.target.checked)}
                disabled={isCustomPrompt}
                sx={{ '&.Mui-checked': { color: '#FF6B35' } }}
              />
            }
            label={
              <Typography sx={{ fontSize: '14px', color: gray80 }}>
                {itemValidationLabel.required}
              </Typography>
            }
          />

          {/* 0を空欄として扱う（#3262）。必須の補助設定なので、
              数字型かつ必須ONのときだけ表示する。
              テキストで文字列 "0" を空欄扱いする場面は無いため型でも絞る */}
          {showZeroAsEmpty && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={treatZeroAsEmpty}
                  onChange={(e): void => setTreatZeroAsEmpty(e.target.checked)}
                  disabled={isCustomPrompt}
                  sx={{ '&.Mui-checked': { color: '#FF6B35' } }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: '14px', color: gray80 }}>
                    {itemValidationLabel.treatZeroAsEmpty}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: gray60 }}>
                    {itemValidationHelperText.treatZeroAsEmpty}
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', marginTop: '4px' }}
            />
          )}

          {showLengthConstraint && (
            <Box sx={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
              <CustomTextfield
                labelText={itemValidationLabel.minLength}
                value={minLength}
                onChange={(e): void => setMinLength(e.target.value.replace(/[^0-9]/g, ''))}
                inputWidth="100%"
                height="56px"
                placeholder="例: 1"
                disabled={isCustomPrompt}
              />
              <CustomTextfield
                labelText={itemValidationLabel.maxLength}
                value={maxLength}
                onChange={(e): void => setMaxLength(e.target.value.replace(/[^0-9]/g, ''))}
                inputWidth="100%"
                height="56px"
                placeholder="例: 100"
                disabled={isCustomPrompt}
              />
            </Box>
          )}

          {showValueRangeConstraint && (
            <Box sx={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
              <CustomTextfield
                labelText={itemValidationLabel.minValue}
                value={minValue}
                onChange={(e): void => setMinValue(e.target.value.replace(/[^0-9.-]/g, ''))}
                inputWidth="100%"
                height="56px"
                placeholder="例: 1"
                disabled={isCustomPrompt}
              />
              <CustomTextfield
                labelText={itemValidationLabel.maxValue}
                value={maxValue}
                onChange={(e): void => setMaxValue(e.target.value.replace(/[^0-9.-]/g, ''))}
                inputWidth="100%"
                height="56px"
                placeholder="例: 1000"
                disabled={isCustomPrompt}
              />
            </Box>
          )}

          {/* 文字種・入力パターン・日付範囲（#3357）。UI は子コンポーネントに切り出している */}
          <AdvancedValidationSettings
            showCharType={showCharTypeConstraint}
            showDateRange={showDateRangeConstraint}
            disabled={isCustomPrompt}
            charType={charType}
            onCharTypeChange={setCharType}
            pattern={pattern}
            onPatternChange={setPattern}
            patternError={patternError}
            patternMessage={patternMessage}
            onPatternMessageChange={setPatternMessage}
            dateNoPast={dateNoPast}
            onDateNoPastChange={setDateNoPast}
            dateNoFuture={dateNoFuture}
            onDateNoFutureChange={setDateNoFuture}
            datePastLimit={datePastLimit}
            onDatePastLimitChange={setDatePastLimit}
            datePastUnit={datePastUnit}
            onDatePastUnitChange={setDatePastUnit}
            dateFutureLimit={dateFutureLimit}
            onDateFutureLimitChange={setDateFutureLimit}
            dateFutureUnit={dateFutureUnit}
            onDateFutureUnitChange={setDateFutureUnit}
          />

          {/* 要注意にする選択肢（#3357）。チェックボックスは現在の選択肢から作るため、
              選択肢を改名・削除しても取り残されない。選択肢が未設定なら出さない */}
          {showReviewChoice && reviewChoiceOptions.length > 0 && (
            <Box sx={{ marginTop: '12px' }}>
              <Typography sx={{ fontSize: '14px', color: gray80 }}>
                {itemValidationLabel.reviewChoice}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: gray60, marginBottom: '4px' }}>
                {itemValidationHelperText.reviewChoice}
              </Typography>
              {reviewChoiceOptions.map(choice => {
                // 出力値が空なら表示名を値として使う（#3075 の既定）
                const choiceValue = choice.value || choice.label;
                return (
                  <FormControlLabel
                    key={choiceValue}
                    control={
                      <Checkbox
                        checked={reviewChoiceValues.includes(choiceValue)}
                        onChange={(e): void =>
                          setReviewChoiceValues(prev =>
                            e.target.checked
                              ? [...prev, choiceValue]
                              : prev.filter(v => v !== choiceValue),
                          )
                        }
                        disabled={isCustomPrompt}
                        sx={{ '&.Mui-checked': { color: '#FF6B35' } }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '14px', color: gray80 }}>
                        {choice.label}
                      </Typography>
                    }
                    sx={{ display: 'flex' }}
                  />
                );
              })}
            </Box>
          )}

          {/* 読み取り精度が低いときに要注意にする（#3384）。デフォルト ON。
              バリデーションや照合の要注意は別の設定で止められるため、ここでは読み取り精度だけを切り替える */}
          {showReviewLowConfidence && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={reviewLowConfidence}
                  onChange={(e): void => setReviewLowConfidence(e.target.checked)}
                  disabled={isCustomPrompt}
                  sx={{ '&.Mui-checked': { color: '#FF6B35' } }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: '14px', color: gray80 }}>
                    {itemValidationLabel.reviewLowConfidence}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: gray60 }}>
                    {itemValidationHelperText.reviewLowConfidence}
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', marginTop: '12px' }}
            />
          )}

          {hasValidationError && (
            <Typography sx={{ fontSize: '12px', color: '#d32f2f', marginTop: '6px' }}>
              {lengthRangeError
                ? itemValidationHelperText.lengthRange
                : itemValidationHelperText.valueRange}
            </Typography>
          )}
        </Box>

        {/* 出力設定（#3073）。読み取り結果をどう出力・表示するかの設定をまとめる。
            中身（固定値・列幅）が両方とも非表示になるケースでは、見出しと区切り線も出さない */}
        {showOutputSettings && (
          <>
            <Divider sx={{ borderColor: gray40, marginBottom: '20px' }} />

            <Box sx={{ marginBottom: '32px' }}>
              <Typography sx={{ ...boldMd, color: gray80, marginBottom: '8px' }}>
                出力設定（任意）
              </Typography>

              {/* 固定値。基幹システムのCSV取込フォーマットに合わせて、値が取得できなかった項目に
                  常に決まった値（例: 0）を入れるための設定。
                  複数行データ（テーブル）は行数が可変で固定値を入れる対象が定まらないため表示しない */}
              {showFixedValue && (
                <Box sx={{ marginBottom: itemData?.isChild ? '20px' : 0 }}>
                  <CustomTextfield
                    labelText="固定値（任意）"
                    value={fixedValue}
                    onChange={(e): void => setFixedValue(e.target.value)}
                    inputWidth="100%"
                    height="56px"
                    placeholder={fixedValuePlaceholder(dataTypeValue)}
                    disabled={isCustomPrompt}
                    // AI読取ONのときは読み取れれば読取値が入り、固定値は読み取れなかった場合の
                    // 予備として使われる。誤解を避けるためONのときだけ項目名の下に補足する（#3073）
                    description={
                      isOcrTarget ? 'AI読取ONの場合は、読み取った値が優先されます' : undefined
                    }
                  />
                </Box>
              )}

              {itemData?.isChild && (
                <Box>
                  <CustomSelectField
                    labelText="列幅"
                    list={displayWidthOptions}
                    value={displayWidth}
                    onChange={handleDisplayWidthChange}
                    height="56px"
                    fontSize="16px"
                  />
                  <Typography sx={{ fontSize: '12px', color: gray80, marginTop: '6px' }}>
                    AI読取の結果画面での列の表示幅を設定できます
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}

        {/* フィールドコード（#3175）。モーダルの一番下に置く。
            計算フィールド（#3145）・指定フォーマット出力（#3070）から項目を指定するための識別子。
            通常の CSV/Excel 出力のヘッダー名とは別物（そちらは #3158）。
            項目名と違い変更されると参照が壊れるため、鉛筆アイコンを押すまでは非活性にする（kintone と同じ）。
            編集中は「設定」「キャンセル」を出し、「設定」でモーダル内の値として確定する。
            この時点では Firestore に書かず、最終的な保存はパターンの保存時に行う。 */}
        <Divider sx={{ borderColor: gray40, marginBottom: '20px' }} />

        <Box sx={{ marginBottom: '8px' }}>
          {/* ラベルは CustomTextfield に持たせず自前で出す。
              ラベルを内包させると「ラベル＋入力欄」全体が flex の対象になり、
              鉛筆アイコンがラベルを含めた高さの中央に来てしまうため。
              ここで分離することで、入力欄と鉛筆アイコンが同じ行の兄弟要素になり、
              alignItems: center が入力欄だけに効く。 */}
          {/* fontSize は CustomTextfield 内のラベル（styles.label）と同じ14pxに揃える。
              formLabelWrapper の12pxは外枠用で、実際のラベル文字は14pxで描画されるため。 */}
          <Typography sx={{ ...formLabelWrapper, fontSize: '14px', color: gray80 }}>
            フィールドコード
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CustomTextfield
                value={isFieldCodeEditable ? fieldCodeDraft : fieldCode}
                onChange={(e): void => setFieldCodeDraft(e.target.value)}
                inputWidth="100%"
                height="56px"
                placeholder="例: 単価"
                disabled={isCustomPrompt || !isFieldCodeEditable}
                error={fieldCodeError !== null}
                formMb="0px"
              />
            </Box>
            {!isFieldCodeEditable && (
              <Tooltip title="フィールドコードを編集する">
                {/* Tooltip は disabled な子要素のイベントを拾えないため span で包む */}
                <span>
                  <IconButton
                    onClick={(): void => setIsFieldCodeEditable(true)}
                    disabled={isCustomPrompt}
                    aria-label="フィールドコードを編集する"
                  >
                    <EditIcon sx={{ fontSize: '20px', color: gray80 }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>

          {/* 編集中のみ表示。「設定」を押すまで確定しない（その場では保存されないことを示す文言にする）。
              入力欄は formMb="0px" で下マージンを消しているため、ここで間隔を確保する */}
          {isFieldCodeEditable && (
            <Box
              sx={{
                display: 'flex',
                gap: '8px',
                justifyContent: 'flex-end',
                marginTop: '12px',
              }}
            >
              <Button
                sx={{ ...styles.cancelButton, height: '36px', flex: 'none', minWidth: '96px' }}
                onClick={handleCancelFieldCode}
              >
                <Typography sx={{ ...regularSm, color: gray80 }}>キャンセル</Typography>
              </Button>
              <Button
                sx={{ ...styles.saveButton, height: '36px', flex: 'none', minWidth: '96px' }}
                onClick={handleApplyFieldCode}
                disabled={fieldCodeError !== null}
              >
                <Typography sx={{ ...regularSm, color: white }}>設定</Typography>
              </Button>
            </Box>
          )}
          <Typography
            sx={{
              fontSize: '12px',
              color: fieldCodeError !== null ? '#d32f2f' : gray80,
              marginTop: '6px',
              lineHeight: '1.8',
            }}
          >
            {fieldCodeHelperText}
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.buttonWrapper}>
        <Button sx={styles.cancelButton} onClick={onClose}>
          <Typography sx={styles.cancelButtonText}>キャンセル</Typography>
        </Button>
        {onSaveAndReOcr ? (
          <>
            <Button
              sx={styles.saveButton}
              onClick={handleSave}
              disabled={!isValid || !hasAnyChange}
            >
              <Typography sx={styles.saveButtonText}>保存する</Typography>
            </Button>
            {/* AI読取OFF（isOcrTarget=false）の項目はAIで読み取らないため再読取は非活性にする（#3054）。
                disabledなButtonはhoverイベントが発火しないため、理由表示のツールチップはBoxで包んで受ける */}
            <Tooltip title={!isOcrTarget ? 'AI読取OFFの項目は再読取できません' : ''}>
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
                <Button
                  sx={styles.saveButton}
                  onClick={handleSaveAndReOcr}
                  disabled={!isValid || !!isCustomPrompt || !isOcrTarget}
                >
                  <Typography sx={styles.saveButtonText}>保存して再読取</Typography>
                </Button>
              </Box>
            </Tooltip>
          </>
        ) : (
          <Button sx={styles.saveButton} onClick={handleSave} disabled={!isValid}>
            <Typography sx={styles.saveButtonText}>
              {saveButtonLabel ?? (isNonReReadChange ? '保存する' : '再読取する')}
            </Typography>
          </Button>
        )}
      </Box>
    </Dialog>
  );
};

export default ItemEditModal;
